import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

export default function Layout() {
  const { staff, logout } = useAuth();
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [menuDropdownOpen, setMenuDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  const menuDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    if (!confirm('Are you sure you want to logout?')) return;
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setMenuDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-pampa-50 flex flex-col">
      <header className="h-16 bg-grey-900 border-b border-grey-800 sticky top-0 z-20">
        <div className="h-full px-4 lg:px-8 flex items-center">
          <Link to="/" className="mr-auto">
            <span className="hidden lg:inline text-xl font-semibold text-white tracking-wider">Sharre Support</span>
            <span className="lg:hidden text-xl font-semibold text-white tracking-wider">SS</span>
          </Link>

          <div className="flex items-center space-x-4">
            <div className="relative" ref={menuDropdownRef}>
              <button
                onClick={() => setMenuDropdownOpen(!menuDropdownOpen)}
                className="p-2 rounded-lg hover:bg-grey-800 transition"
              >
                <svg className="w-6 h-6 text-grey-400 hover:text-white transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {menuDropdownOpen && (
                <div className="fixed left-0 right-0 bg-grey-900 border-b border-grey-800 shadow-xl overflow-y-auto" style={{ top: '64px', maxHeight: '40vh' }}>
                  <div className="container mx-auto px-4 lg:px-8 py-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {menuItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMenuDropdownOpen(false)}
                          className={`flex flex-col items-center justify-center p-4 rounded-lg transition font-medium tracking-wide border-2 ${
                            isActive(item.path)
                              ? 'bg-grey-800 text-white border-white'
                              : 'text-grey-400 hover:bg-grey-800 hover:text-white border-transparent'
                          }`}
                        >
                          <div className="mb-2">{item.icon}</div>
                          <span className="text-xs text-center">{item.name}</span>
                        </Link>
                      ))}

                      <button
                        onClick={() => {
                          handleLogout();
                          setMenuDropdownOpen(false);
                        }}
                        className="flex flex-col items-center justify-center p-4 rounded-lg text-grey-400 hover:bg-red-900/30 hover:text-red-400 transition font-medium tracking-wide border-2 border-transparent hover:border-red-400"
                      >
                        <div className="mb-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        </div>
                        <span className="text-xs text-center">Logout</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-grey-800 transition"
              >
                <div className="w-9 h-9 rounded-full bg-grey-700 flex items-center justify-center hover:bg-grey-600 transition">
                  <span className="text-base font-medium text-white">
                    {staff?.full_name?.charAt(0).toUpperCase() || 'S'}
                  </span>
                </div>
                {userDropdownOpen && (
                  <svg className="w-4 h-4 text-grey-400 transition rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-pampa-200 py-2">
                  <div className="px-4 py-3 border-b border-pampa-200">
                    <p className="text-sm font-medium text-grey-900">{staff?.full_name || 'Support Staff'}</p>
                    <p className="text-xs font-medium text-grey-500 mt-0.5">{staff?.email || ''}</p>
                  </div>

                  <button
                    onClick={() => {
                      handleLogout();
                      setUserDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm font-medium text-grey-700 hover:bg-pampa-50 transition tracking-wide"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
